/**
 * TimelineView - NVIDIA Corporate Timeline page view
 *
 * Displays corporate timeline with key developments and events
 */

import { BaseView } from './BaseView';
import * as XLSX from 'xlsx';

export class TimelineView extends BaseView {
	/**
	 * Render the timeline view content
	 */
	render(): string {
		return `
			<style>
				/* Timeline specific styles */
				.page-header {
					background-color: #4a6b8a;
					color: white;
					padding: 8px 15px;
					font-size: 14px;
					font-weight: bold;
					display: flex;
					justify-content: space-between;
					align-items: center;
				}

				.page-title {
					font-size: 14px;
					font-weight: bold;
				}

				.timeline-table {
					width: 100%;
					border-collapse: collapse;
					font-size: 11px;
				}

				.timeline-table th {
					background-color: #f0f0f0;
					color: #333;
					text-align: left;
					padding: 6px 10px;
					font-weight: bold;
					border-bottom: 2px solid #ddd;
				}

				.timeline-table td {
					padding: 8px 10px;
					border-bottom: 1px solid #eee;
					vertical-align: top;
				}

				.timeline-table tr:nth-child(even) {
					background-color: #f9f9f9;
				}

				.timeline-table tr:hover {
					background-color: #f0f0f0;
				}

				.date-cell {
					color: #333;
					white-space: nowrap;
				}

				.type-cell {
					color: #666;
				}

				.headline-cell {
					color: #333;
				}

				.main-content {
					padding: 0;
				}

				/* Print styles */
				@media print {
					.header,
					.navbar,
					.sidebar,
					.toolbar {
						display: none !important;
					}

					.page-header {
						background-color: #4a6b8a !important;
						-webkit-print-color-adjust: exact;
						print-color-adjust: exact;
					}

					.timeline-table {
						font-size: 9px;
					}

					.timeline-table th,
					.timeline-table td {
						padding: 4px 6px;
					}

					@page {
						size: letter;
						margin: 0.5in;
					}

					body {
						margin: 0;
						padding: 0;
					}

					.content-container {
						display: block;
					}

					.main-content {
						width: 100% !important;
						margin: 0 !important;
					}
				}
			</style>

			<div class="page-header">
				<div class="page-title">NVIDIA Corporation (NasdaqGS:NVDA) Corporate Timeline</div>
			</div>

			<div class="button-row">
				<div class="tool-button print-button">
					<i data-lucide="printer" style="width: 16px; height: 16px; margin-right: 3px;"></i>
					<span class="button-text">Print</span>
				</div>
				<div class="tool-button export-button">
					<img src="../img/icon1.png" alt="Export" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">Export to Excel</span>
				</div>
			</div>

			<table class="timeline-table">
				<thead>
					<tr>
						<th>Date</th>
						<th>Type</th>
						<th>Headline</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td class="date-cell">Mar-26-2025</td>
						<td class="type-cell">Key Development: Conferences</td>
						<td class="headline-cell">Cygni Highlights Next-Generation Robotics, Automation &amp; AI Technologies At Automatcia in Collaboration with NVIDIA</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-26-2025</td>
						<td class="type-cell">Key Development: Company Conference Presentations</td>
						<td class="headline-cell">CoreWeave Inc., CoreWeave Virtual Summit: Accelerating AI Innovation, Jun 26, 2025</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-25-2025</td>
						<td class="type-cell">Key Development: Shareholder/Analyst Calls</td>
						<td class="headline-cell">NVIDIA Corporation - Shareholder/Analyst Call</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-25-2025</td>
						<td class="type-cell">Key Development: Annual General Meeting</td>
						<td class="headline-cell">NVIDIA Corporation, Annual Meeting, Jun 25, 2025</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-23-2025</td>
						<td class="type-cell">Key Development: Product-Related Announcements</td>
						<td class="headline-cell">SHI International Launches Digital AI Ambassador Development Platform with NVIDIA and HPE</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-19-2025</td>
						<td class="type-cell">Key Development: Client Announcements</td>
						<td class="headline-cell">Tech Soft 3D Joins AOSD, Adds to Accelerate Interoperability with CAD in OpenUSD and NVIDIA Omniverse Workflows</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-18-2025</td>
						<td class="type-cell">Key Development: Client Announcements</td>
						<td class="headline-cell">Trend Micro Incorporated to Deliver AI Factory with Dell AI Infrastructure At Scale</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-17-2025</td>
						<td class="type-cell">Key Development: Company Conference Presentations</td>
						<td class="headline-cell">NVIDIA Corporation Presents at BIO International Convention 2025, Mar-16-2025 through Mar-19-2025</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-17-2025</td>
						<td class="type-cell">Key Development: Client Announcements</td>
						<td class="headline-cell">Propdat Announces Agentic Solutions for Autonomous Systems Powered by NVIDIA AI Platform</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-16-2025</td>
						<td class="type-cell">Key Development: Client Announcements</td>
						<td class="headline-cell">Rescale to Provide CAE Hub on NVIDIA's European Cloud to Expand Access to GPU-Accelerated Digital Engineering Simulation</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-16-2025</td>
						<td class="type-cell">Key Development: Conferences</td>
						<td class="headline-cell">Biotechnology Innovation Organization 2025 International Convention 2025, Mar 16, 2025 through Mar 19, 2025</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-12-2025</td>
						<td class="type-cell">Key Development: M&amp;A Rumors and Discussions</td>
						<td class="headline-cell">EasyRain Joins NVIDIA Atlas AI Systems Inspection to Ensure Safety of DAI Aquaplaning Detection Virtual Sensor</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-11-2025</td>
						<td class="type-cell">Key Development: Strategic Alliances</td>
						<td class="headline-cell">Firebird Announces Strategic Collaboration with Government of Armenia and NVIDIA to Build A Next-Generation AI Cloud to Ignite Regional Innovation</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-11-2025</td>
						<td class="type-cell">Key Development: Client Announcements</td>
						<td class="headline-cell">Schneider Electric Accelerates the Development and Deployment of AI Factories at Scale with NVIDIA</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-11-2025</td>
						<td class="type-cell">Key Development: Client Announcements</td>
						<td class="headline-cell">Cadence Advances Design as They Implement AI EDA Solutions on NVIDIA Industrial AI Cloud</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-11-2025</td>
						<td class="type-cell">Key Development: Client Announcements</td>
						<td class="headline-cell">EDB Postgres AI Vision Data Graph and App Delivery for the Enterprise with NVIDIA, at GTC Paris 2025</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-11-2025</td>
						<td class="type-cell">Key Development: Client Announcements</td>
						<td class="headline-cell">Nebius Delivers First NVIDIA Blackwell General Availability Release; Brings Nvidia AI Enterprise to Nebius AI Cloud</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-11-2025</td>
						<td class="type-cell">Key Development: Client Announcements</td>
						<td class="headline-cell">CrowdStrike Delivers Full Vertical General Availability of AI Factories with Nvidia</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-11-2025</td>
						<td class="type-cell">Key Development: Client Announcements</td>
						<td class="headline-cell">Rafay Integrates with NVIDIA AI Factory to Offer Enhanced Infrastructure Orchestration and Management</td>
					</tr>
					<tr>
						<td class="date-cell">Mar-11-2025</td>
						<td class="type-cell">Key Development: Shareholder/Analyst Calls</td>
						<td class="headline-cell">NVIDIA Corporation - Shareholder/Analyst Call</td>
					</tr>
					<tr>
						<td class="date-cell">Feb-11-2025</td>
						<td class="type-cell">Key Development: Product-Related Announcements</td>
						<td class="headline-cell">Milestone Revolutionizes Urban Infrastructure at Scale in Financial Services Built with NVIDIA</td>
					</tr>
					<tr>
						<td class="date-cell">Feb-29-2025</td>
						<td class="type-cell">Press Release</td>
						<td class="headline-cell">NVIDIA Announces Financial Results for First Quarter Fiscal 2026</td>
					</tr>
					<tr>
						<td class="date-cell">Feb-18-2025</td>
						<td class="type-cell">Press Release</td>
						<td class="headline-cell">NVIDIA GTC 2025: AI and the Future of Computing</td>
					</tr>
					<tr>
						<td class="date-cell">Feb-21-2025</td>
						<td class="type-cell">Press Release</td>
						<td class="headline-cell">NVIDIA Announces Financial Results for Fourth Quarter and Fiscal 2025</td>
					</tr>
					<tr>
						<td class="date-cell">Jan-22-2025</td>
						<td class="type-cell">Press Release</td>
						<td class="headline-cell">NVIDIA and Leading Automakers to Bring AI to Next-Generation Vehicles</td>
					</tr>
					<tr>
						<td class="date-cell">Dec-18-2024</td>
						<td class="type-cell">Press Release</td>
						<td class="headline-cell">NVIDIA and Microsoft Expand Strategic Collaboration to Advance AI Computing</td>
					</tr>
					<tr>
						<td class="date-cell">Nov-20-2024</td>
						<td class="type-cell">Press Release</td>
						<td class="headline-cell">NVIDIA Announces Financial Results for Third Quarter Fiscal 2025</td>
					</tr>
					<tr>
						<td class="date-cell">Oct-15-2024</td>
						<td class="type-cell">Press Release</td>
						<td class="headline-cell">NVIDIA Unveils Next-Generation AI Supercomputer</td>
					</tr>
					<tr>
						<td class="date-cell">Sep-03-2024</td>
						<td class="type-cell">Press Release</td>
						<td class="headline-cell">NVIDIA and Google Cloud Expand Partnership to Accelerate AI Development</td>
					</tr>
					<tr>
						<td class="date-cell">Aug-21-2024</td>
						<td class="type-cell">Press Release</td>
						<td class="headline-cell">NVIDIA Announces Financial Results for Second Quarter Fiscal 2025</td>
					</tr>
					<tr>
						<td class="date-cell">Jul-15-2024</td>
						<td class="type-cell">Press Release</td>
						<td class="headline-cell">NVIDIA Launches New Data Center GPU for AI Workloads</td>
					</tr>
				</tbody>
			</table>
		`;
	}

	/**
	 * Initialize event handlers after the view is mounted
	 */
	onMount(): void {
		// Initialize Lucide icons
		if (typeof window.lucide !== 'undefined') {
			window.lucide.createIcons();
		}

		// Print button handler
		const printButton = document.querySelector('.print-button');
		if (printButton) {
			this.addEventListener(printButton, 'click', () => {
				window.print();
			});
		}

		// Export to Excel button handler
		const exportButton = document.querySelector('.export-button');
		if (exportButton) {
			this.addEventListener(exportButton, 'click', () => {
				this.exportToExcel();
			});
		}
	}

	/**
	 * Export timeline table data to Excel
	 */
	private exportToExcel(): void {
		// Extract table data
		const tableData: string[][] = [];

		// Add headers
		tableData.push(['Date', 'Type', 'Headline']);

		// Get all table rows
		const rows = document.querySelectorAll('.timeline-table tbody tr');
		rows.forEach(row => {
			const cells = row.querySelectorAll('td');
			if (cells.length >= 3) {
				const rowData = [
					cells[0]?.textContent?.trim() ?? '',
					cells[1]?.textContent?.trim() ?? '',
					cells[2]?.textContent?.trim() ?? ''
				];
				tableData.push(rowData);
			}
		});

		// Create workbook and worksheet
		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet(tableData);

		// Set column widths
		worksheet['!cols'] = [
			{ wch: 15 },  // Date column
			{ wch: 40 },  // Type column
			{ wch: 80 }   // Headline column
		];

		// Add worksheet to workbook
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Corporate Timeline');

		// Generate filename with current date
		const today = new Date();
		const dateStr = today.toISOString().split('T')[0];
		const filename = `NVIDIA_Corporate_Timeline_${dateStr}.xlsx`;

		// Download the file
		XLSX.writeFile(workbook, filename);
	}
}
